import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { eventTraceConfig, eventTraceSortKeys, eventTraceQueryAliases, eventTraceDateField } from "./eventTraceConfig";
import { useTableQuery } from "hooks/useTableQuery";
import { ListRefreshIndicator } from "components/CustomComponents/StandardListControls";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { EventTraceStatusCards } from "./EventTraceStatusCards";
import { EventTraceDateRangeFilter } from "./EventTraceDateRangeFilter";
import { useHistory } from "react-router-dom";
import * as Mui from "@mui/material";
import PlayArrowRounded from "@mui/icons-material/PlayArrowRounded";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import CheckRounded from "@mui/icons-material/CheckRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import RemoveRounded from "@mui/icons-material/RemoveRounded";
import PersonOutlineRounded from "@mui/icons-material/PersonOutlineRounded";
import AccessTimeOutlined from "@mui/icons-material/AccessTimeOutlined";
import MoveToInboxOutlined from "@mui/icons-material/MoveToInboxOutlined";
import DnsOutlined from "@mui/icons-material/DnsOutlined";
import SwapHorizOutlined from "@mui/icons-material/SwapHorizOutlined";
import AccountTreeOutlined from "@mui/icons-material/AccountTreeOutlined";
import ToggleOnOutlined from "@mui/icons-material/ToggleOnOutlined";
import FingerprintOutlined from "@mui/icons-material/FingerprintOutlined";
import TagOutlined from "@mui/icons-material/TagOutlined";
import PlaceOutlined from "@mui/icons-material/PlaceOutlined";
import { designColors, designFontFamily, designShell, designTheme, designType, paginationFooter } from "services/constants/designTokens";
import { GlobalBreadcrumb, sharedStickyCustomFilterBarSx, sharedStickyFooterBarSx } from "components/DesignSystem/sharedUiPatterns";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { RequestDetailField, detailAccentPalette, detailCardSx, detailSectionLabelSx } from "components/DesignSystem/RequestDetailField";
import { customAPI, customGetAPI } from "services/utils/CustomAPI";
import { showToast } from "services/utils/Status";
import { withScreenPermissions } from "components/DesignSystem/ScreenAccess";
import { SCREEN_ACCESS } from "services/utils/screenPermissions";
import { Loader } from "components/Loader";
import { PrimaryButton, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { FilterProvider } from "pages/FilterContext";
import { StatusBadge } from "services/constants";
import { contactInitials } from "pages/Administration/SupportContacts/supportContactsData";
import { ApproveTable } from "pages/TeamZone/Approve/approveUi";
import { eventTraceDetailUrl, mapEventDetail, mapEventRow } from "./eventData";
import { balancedColumnWidth, monitoringBodyCellSx, monitoringHeadCellSx, monitoringNameSx, monitoringRowSx, useMeasuredHeight, useMonitoringListAccess } from "./listUi";
const tones = {
  Completed: "#059669",
  Retry: "#D97706",
  Quarantined: "#EF4444",
  Failed: "#DC2626",
  Queued: "#2563EB",
  Processing: "#2563EB",
  Pending: "#64748B",
  Ignored: "#64748B",
  IN: "#059669",
  OUT: "#EF4444"
};
// Statuses the API may add later fall back to neutral grey.
const tone = status => tones[status] || "#64748B";
const Badge = ({
  status
}) => <RequestStatusChip status={status} colorCode={tone(status)} />;
const formatTime = value => !value || Number.isNaN(new Date(value).getTime()) ? "—" : new Date(value).toLocaleString("en-GB", {
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false
});
// Panel section label: the shared detail-panel label, shown in title case
// like every other detail panel, at the 15px panel section title size used
// by the Administration detail panels (the shared 13px label reads too small
// here, where most headings stand alone outside a card).
const panelSectionLabelSx = {
  ...detailSectionLabelSx,
  fontSize: 15,
  mb: 0
};
// Details panel rows, on the shared icon + label + value field used by the
// Team Workspace and Administration detail panels.
const eventFields = event => [
  { icon: DnsOutlined, label: "Device", value: event.deviceId ? `${event.device} (${event.deviceId})` : event.device, accent: detailAccentPalette.blue },
  { icon: PlaceOutlined, label: "Location", value: event.location, accent: detailAccentPalette.green },
  { icon: FingerprintOutlined, label: "Source", value: event.source, accent: detailAccentPalette.purple },
  { icon: SwapHorizOutlined, label: "Direction", value: event.direction, accent: event.direction === "IN" ? detailAccentPalette.green : detailAccentPalette.pink },
  { icon: AccessTimeOutlined, label: "Event Time", value: formatTime(event.eventTime), accent: detailAccentPalette.purple },
  { icon: MoveToInboxOutlined, label: "Received Time", value: formatTime(event.receivedTime), accent: detailAccentPalette.orange },
  { icon: AccountTreeOutlined, label: "Current Stage", value: event.stageName, accent: detailAccentPalette.blue },
  // Only present once the event has reached those stages.
  ...(event.punchEntryId !== null ? [{ icon: TagOutlined, label: "Punch Entry ID", value: String(event.punchEntryId), accent: detailAccentPalette.green }] : []),
  ...(event.attendanceRecordId !== null ? [{ icon: TagOutlined, label: "Attendance Record ID", value: String(event.attendanceRecordId), accent: detailAccentPalette.green }] : [])
];
// Standard list headers: drive the table columns, the CustomFilter toolbar
// (search / sort / filter / download) and the "Default View" column picker.
// `employee` is a plain-text field derived per row (see mapEventRow in
// eventData.js) so search, filters and exports work on what is displayed.
const eventHeaders = [
  { name: "Event ID", key: "globalEventId", type: "number", icon: <TagOutlined fontSize="small" /> },
  { name: "Time", key: "eventTime", type: "date", customFormatter: (event) => formatTime(event.eventTime), icon: <AccessTimeOutlined fontSize="small" /> },
  { name: "Employee", key: "employee", type: "string", icon: <PersonOutlineRounded fontSize="small" /> },
  { name: "Device", key: "device", type: "autocomplete", icon: <DnsOutlined fontSize="small" /> },
  { name: "Direction", key: "direction", type: "autocomplete", icon: <SwapHorizOutlined fontSize="small" /> },
  { name: "Current Stage", key: "stageName", type: "autocomplete", icon: <AccountTreeOutlined fontSize="small" /> },
  { name: "Status", key: "status", type: "autocomplete", icon: <ToggleOnOutlined fontSize="small" /> },
  { name: "Action", key: "" },
];
// The Action column has no data behind it, so it is left out of the
// "Add Filter" / "Add Sort" pickers.
// Fixed-layout column widths; "Employee" takes whatever is left, so the
// table always fits its card and never needs a horizontal scrollbar.
const ACTION_COLUMN_WIDTH = 160;
const columnWidths = {
  globalEventId: "8%",
  eventTime: "15%",
  device: "13%",
  direction: "9%",
  stageName: "15%",
  status: "13%",
  "": ACTION_COLUMN_WIDTH
};
const cellSx = monitoringBodyCellSx;
// The shared body cell truncates long text with an ellipsis. The Action cell
// holds a button, not text: if the button is even a pixel wider than the
// cell, the browser paints that ellipsis ("...") beside it. Clip instead.
const actionCellSx = {
  ...cellSx,
  textOverflow: "clip"
};
const actionIcon = label => /reprocess/i.test(label) ? <RefreshRounded /> : /process/i.test(label) ? <PlayArrowRounded /> : undefined;
const isReprocess = label => String(label).trim().toLowerCase() === "reprocess";
// A completed event has nothing left to reprocess.
const isCompleted = event => String(event?.status ?? "").trim().toLowerCase() === "completed";
const cellText = (key, event) => key === "eventTime" ? formatTime(event.eventTime) : key === "globalEventId" ? event.globalEventId ?? "—" : key === "" ? event.action || undefined : event[key];
export function EventTrace({ permissions }) {
  // Reprocess changes the event, so it needs the edit permission. Without
  // it the action buttons are not shown at all.
  const canEdit = permissions?.canEdit !== false;
  const history = useHistory();
  const { domain, userDetails, subModuleId, canSaveViews, topbarCompanyName } = useMonitoringListAccess("Event Trace");
  const table = useTableQuery({ endpoint: `${domain}monitoring/event-trace/`, refreshInterval: 15000, queryAliases: eventTraceQueryAliases });
  const setTableParams = table.setParams;
  const rows = useMemo(() => (table.data?.results || []).map(mapEventRow), [table.data]);
  const total = table.data?.pagination?.total_count ?? 0;
  const page = (table.data?.pagination?.page || Number(table.state.page) || 1) - 1;
  const pageSize = Number(table.state.page_size || 25);
  const setPage = useCallback(next => setTableParams({ page: next + 1 }, { resetPage: false }), [setTableParams]);
  const setPageSize = useCallback(next => setTableParams({ page_size: next }), [setTableParams]);
  const { loading, ready } = table;
  const failed = Boolean(table.error);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailFailed, setDetailFailed] = useState(false);
  const [detailReloadKey, setDetailReloadKey] = useState(0);
  const [reprocessingId, setReprocessingId] = useState(null);
  const reprocessingRef = useRef(false);
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  const [customFilterRef, customFilterHeight] = useMeasuredHeight();
  const [footerRef, footerHeight] = useMeasuredHeight();
  // The side panel loads the full event (journey, reason, actions)
  // from the detail endpoint each time a row is opened.
  useEffect(() => {
    setDetail(null);
    setDetailFailed(false);
    if (selectedId === null) {
      setDetailLoading(false);
      return undefined;
    }
    let active = true;
    setDetailLoading(true);
    customGetAPI(eventTraceDetailUrl(domain, selectedId)).then(result => {
      if (!active) return;
      if (result?.ok) setDetail(mapEventDetail(result.responseDetails));else setDetailFailed(true);
      setDetailLoading(false);
    }).catch(() => {
      if (!active) return;
      setDetailFailed(true);
      setDetailLoading(false);
    });
    return () => {
      active = false;
    };
  }, [domain, selectedId, detailReloadKey]);
  // Until the detail arrives, the panel header shows what the list row has.
  const selected = detail || rows.find(event => event.id === selectedId) || null;
  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "monitoring_event_trace_saved_views",
    fullHeaders: eventHeaders,
    screenAccess: canSaveViews
  });
  const visibleEvents = rows;
  const refresh = table.refresh;
  const reprocessEvent = async event => {
    if (reprocessingRef.current || isCompleted(event) || event?.id == null || event?.globalEventId == null) return;
    reprocessingRef.current = true;
    setReprocessingId(event.id);
    try {
      const result = await customAPI(eventTraceDetailUrl(domain, event.id), "PUT", { global_event_id: event.globalEventId });
      if (!mountedRef.current) return;
      if (result?.ok) {
        showToast("success", "Event reprocessing requested.");
        refresh();
        if (selectedId === event.id) setDetailReloadKey(key => key + 1);
      }
      // customAPI displays server and network errors.
    } catch (error) {
      if (mountedRef.current) showToast("error", error.message || "Event could not be reprocessed.");
    } finally {
      reprocessingRef.current = false;
      if (mountedRef.current) setReprocessingId(null);
    }
  };
  const actionDisabled = (label, event) => !isReprocess(label) || isCompleted(event) || event?.id == null || event?.globalEventId == null || reprocessingId !== null;
  const actionLabel = (label, event) => isReprocess(label) && reprocessingId === event?.id ? "Reprocessing..." : label;
  const renderCell = (key, event) => {
    switch (key) {
      case "employee":
        return <Mui.Stack direction="row" spacing={1} alignItems="center" sx={{
          minWidth: 0
        }}><Mui.Avatar sx={{
            width: 28,
            height: 28,
            fontSize: 12
          }}>{contactInitials(event.name)}</Mui.Avatar><Mui.Box sx={{
            minWidth: 0
          }}><Mui.Typography noWrap sx={monitoringNameSx(selectedId === event.id)}>{event.name}</Mui.Typography><Mui.Typography noWrap sx={{
              fontSize: 12.5,
              color: designColors.slate600
            }}>{event.employeeId}</Mui.Typography></Mui.Box></Mui.Stack>;
      case "direction":
      case "status":
        return <StatusBadge color={tone(event[key])} label={event[key]} />;
      case "":
        return event.action && canEdit ? <SecondaryButton disabled={actionDisabled(event.action, event)} onClick={click => {
            click.stopPropagation();
            reprocessEvent(event);
          }} startIcon={actionIcon(event.action)} sx={{
            // Fills the fixed-width Action column, so it can never be wider
            // than its cell whatever the label is.
            width: "100%",
            minWidth: 0,
            minHeight: 34,
            fontSize: 12.5,
            px: 1,
            whiteSpace: "nowrap",
            "& .MuiButton-startIcon": {
              mr: 0.5,
              "& > *:nth-of-type(1)": { fontSize: 16 }
            }
          }}>{actionLabel(event.action, event)}</SecondaryButton> : "—";
      default:
        return cellText(key, event);
    }
  };
  if (!ready) return <Loader info="Loading..." />;
  return <Mui.ThemeProvider theme={designTheme}>
    <FilterProvider domain={domain} userDetails={userDetails} subModuleId={subModuleId}>
    <Mui.Box sx={{
      ...designShell.pageSurface,
      fontFamily: designFontFamily,
      p: {
        xs: 2,
        md: 3
      }
    }}><Mui.Stack spacing={2} aria-busy={loading}>
      <GlobalBreadcrumb trail={[{
          label: "Monitoring",
          onClick: () => history.push("/admin/monitoring/dashboard")
        }, {
          label: "Event Trace"
        }]} />
      <Mui.Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" },
        alignItems: { xs: "stretch", md: "center" }, justifyContent: "space-between", gap: 2 }}>
        <Mui.Box><Mui.Typography component="h1" sx={designType.pageTitle}>Event Trace</Mui.Typography><Mui.Typography sx={designType.pageSubtitle}>Trace the journey of a punch event from receipt to attendance processing.</Mui.Typography></Mui.Box>
        <Mui.Box sx={{ display: "flex", justifyContent: "flex-end", flexShrink: 0 }}>
          <EventTraceDateRangeFilter table={table} />
        </Mui.Box>
      </Mui.Box>
      <EventTraceStatusCards counts={table.data?.counts} status={table.state.status}
        onSelect={status => table.setParams({ status })} />
      {failed && !loading && <Mui.Alert severity="error" sx={{
          borderRadius: 2
        }} action={<Mui.Button color="inherit" size="small" onClick={refresh}>Retry</Mui.Button>}>{table.error}</Mui.Alert>}
      {table.newEvents && <Mui.Alert severity="info" action={<Mui.Button onClick={table.refresh}>Refresh</Mui.Button>}>New events — Refresh</Mui.Alert>}
      <Mui.Box ref={customFilterRef} sx={{
          ...designShell.sectionCard,
          ...sharedStickyCustomFilterBarSx
        }}>
        <CustomFilter serverTable={table} serverConfig={eventTraceConfig(domain)} titleOverride={titleOverride} data={rows}
          hideDefaultDateChip
          pickerDateField={eventTraceDateField(table.state)}
          serverExport={{ endpoint: `${domain}monitoring/event-trace/export/`, headers: visibleHeaders,
            reportEndpoint: `${domain}monitoring/event-trace/`, queryAliases: eventTraceQueryAliases,
            metadata: { "Company Name": topbarCompanyName || "All" },
            mapRow: mapEventRow, filename: "Event Trace" }} />
      </Mui.Box>
      <Mui.Box sx={{
          ...designShell.sectionCard,
          width: "100%"
        }}>
        <Mui.TableContainer sx={{
            width: "100%",
            // Visible, not auto: the sticky header needs the page (not this
            // container) as its scroll parent, and the fixed-layout table
            // below always fits the card, so there is nothing to scroll.
            overflowX: "visible",
            pb: footerHeight ? `${footerHeight}px` : 0
          }}>
          <ApproveTable stickyHeader aria-label="Monitoring event trace" sx={{
              width: "100%",
              tableLayout: "fixed"
            }}>
            <Mui.TableHead sx={designShell.tableStickyHead}><Mui.TableRow sx={designShell.tableStickyHeadRow}>{visibleHeaders.map(header => <Mui.TableCell key={header.key || "actions"} scope="col" title={header.name} sortDirection={(table.state.sort_by || "event_time") === eventTraceSortKeys[header.key] ? table.state.sort_order || "desc" : false} sx={{
                  ...monitoringHeadCellSx(customFilterHeight),
                  width: balancedColumnWidth(header.key, visibleHeaders, eventHeaders, columnWidths)
                }}>{eventTraceSortKeys[header.key] ? <Mui.TableSortLabel
                  active={(table.state.sort_by || "event_time") === eventTraceSortKeys[header.key]}
                  direction={table.state.sort_order || "desc"}
                  onClick={() => { const sort = eventTraceSortKeys[header.key]; table.setParams({ sort_by: sort, sort_order: (table.state.sort_by || "event_time") === sort && (table.state.sort_order || "desc") === "desc" ? "asc" : "desc" }); }}>
                  {header.name}</Mui.TableSortLabel> : header.name}</Mui.TableCell>)}</Mui.TableRow></Mui.TableHead>
            <Mui.TableBody>{loading ? // Changing page keeps the toolbar (and its filters) mounted; only
              // the rows are replaced by the Loader.
              <Mui.TableRow><Mui.TableCell colSpan={visibleHeaders.length} sx={{
                  borderBottom: "none"
                }}><Loader info="Loading..." minHeight="40vh" /></Mui.TableCell></Mui.TableRow> : visibleEvents.length === 0 ? <TableEmptyState dataLength={rows} colSpan={visibleHeaders.length} /> : visibleEvents.map(event => <Mui.TableRow key={event.id} onClick={() => setSelectedId(event.id)} sx={monitoringRowSx(selectedId === event.id)}>{visibleHeaders.map(header => <Mui.TableCell key={header.key || "actions"} title={cellText(header.key, event)} sx={header.key === "" ? actionCellSx : cellSx}>{renderCell(header.key, event)}</Mui.TableCell>)}</Mui.TableRow>)}</Mui.TableBody>
          </ApproveTable>
        </Mui.TableContainer>
        <Mui.Box ref={footerRef} sx={sharedStickyFooterBarSx}>{paginationFooter({
            page,
            rowsPerPage: pageSize,
            total,
            countIsEstimate: table.data?.pagination.count_is_estimate,
            itemLabel: "events",
            summaryExtra: <ListRefreshIndicator table={table} sx={{ px: 0, whiteSpace: "nowrap" }} />,
            rowsPerPageOptions: [25, 50, 100],
            onPageChange: setPage,
            onRowsPerPageChange: event => {
              setPageSize(Number(event.target.value));
            }
          })}</Mui.Box>
      </Mui.Box>
    </Mui.Stack></Mui.Box>
    </FilterProvider>
    <GlobalSidePanel open={selectedId !== null} onClose={() => setSelectedId(null)} title="Event Details" subtitle={selectedId !== null ? `Event #${selected?.globalEventId ?? selectedId}` : undefined} bodySx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}>
      {selectedId !== null && <Mui.Stack spacing={2.5} aria-busy={detailLoading}>
        {selected && <Mui.Stack direction="row" spacing={1.5} alignItems="center" sx={{
          p: 2,
          bgcolor: designColors.slateBg,
          borderRadius: 2
        }}><Mui.Avatar sx={{
            bgcolor: designColors.tealTint,
            color: designColors.tealDark
          }}><PersonOutlineRounded /></Mui.Avatar><Mui.Box sx={{
            flex: 1,
            minWidth: 0
          }}><Mui.Typography sx={designType.panelTitle}>{selected.name} <Mui.Box component="span" sx={{
                ...designType.helperText,
                fontWeight: 400
              }}>{selected.employeeId}</Mui.Box></Mui.Typography><Mui.Typography sx={designType.helperText}>{selected.device} · {formatTime(selected.eventTime)}</Mui.Typography></Mui.Box><Badge status={selected.status} /></Mui.Stack>}
        {detailLoading && <Loader info="Loading..." minHeight="40vh" />}
        {detailFailed && !detailLoading && <Mui.Alert severity="error" action={<Mui.Button color="inherit" size="small" onClick={() => setDetailReloadKey(key => key + 1)}>Retry</Mui.Button>}>Event details could not be loaded.</Mui.Alert>}
        {detail && <>
        <Mui.Box sx={detailCardSx}><Mui.Typography component="h2" sx={{
            ...panelSectionLabelSx,
            mb: 1
          }}>Event Information</Mui.Typography>{eventFields(detail).map((field, index, fields) => <RequestDetailField key={field.label} {...field} isLast={index === fields.length - 1} />)}</Mui.Box>
        {detail.reason && <Mui.Alert severity={["Quarantined", "Failed"].includes(detail.status) ? "error" : detail.status === "Retry" ? "warning" : "info"}><Mui.AlertTitle>{detail.reason.title}</Mui.AlertTitle>{detail.reason.detail}</Mui.Alert>}
        <Mui.Divider /><Mui.Typography component="h2" sx={panelSectionLabelSx}>Processing Journey</Mui.Typography>
        <Mui.Box component="ol" sx={{
          m: 0,
          p: 0,
          listStyle: "none"
        }}>{detail.journey.map((step, index, steps) => <Mui.Box component="li" key={step.step} sx={{
            display: "flex",
            gap: 1.5,
            pb: index === steps.length - 1 ? 0 : 1.5,
            position: "relative",
            "&:not(:last-child)::before": {
              content: '""',
              position: "absolute",
              left: 13,
              top: 28,
              bottom: 0,
              width: 2,
              bgcolor: designColors.slate200
            }
          }}><Mui.Avatar sx={{
              width: 28,
              height: 28,
              flexShrink: 0,
              bgcolor: tone(step.status),
              zIndex: 1
            }}>{step.status === "Completed" ? <CheckRounded sx={{
                fontSize: 18
              }} /> : ["Failed", "Quarantined"].includes(step.status) ? <CloseRounded sx={{
                fontSize: 18
              }} /> : <RemoveRounded sx={{
                fontSize: 18
              }} />}</Mui.Avatar><Mui.Box sx={{
              flex: 1,
              minWidth: 0,
              ...designShell.sectionCard,
              p: 1.5,
              bgcolor: step.status === "Quarantined" || step.status === "Failed" ? "#FEF2F2" : designColors.slateBg
            }}><Mui.Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center"><Mui.Typography sx={{
                  fontSize: 13,
                  fontWeight: 600
                }}>{step.step}. {step.name}</Mui.Typography><Badge status={step.status} /></Mui.Stack>{step.time && <Mui.Typography sx={{
                fontSize: 12.5,
                color: designColors.slate600,
                mt: 0.5
              }}>{formatTime(step.time)}</Mui.Typography>}{step.message && <Mui.Typography sx={{
                fontSize: 12.5,
                color: designColors.slate600,
                mt: 0.5
              }}>{step.message}</Mui.Typography>}</Mui.Box></Mui.Box>)}</Mui.Box>
        {canEdit && detail.actions.length > 0 && <>
          <Mui.Typography component="h2" sx={panelSectionLabelSx}>Actions</Mui.Typography><Mui.Stack direction="row" spacing={1}>{detail.actions.map((label, index) => index === 0 ? <PrimaryButton key={label} disabled={actionDisabled(label, detail)} onClick={() => reprocessEvent(detail)} startIcon={actionIcon(label)} sx={{
              flex: 1,
              minWidth: 0
            }}>{actionLabel(label, detail)}</PrimaryButton> : <SecondaryButton key={label} disabled={actionDisabled(label, detail)} onClick={() => reprocessEvent(detail)} startIcon={actionIcon(label)} sx={{
              minWidth: 100
            }}>{actionLabel(label, detail)}</SecondaryButton>)}</Mui.Stack>
        </>}
        </>}
      </Mui.Stack>}
    </GlobalSidePanel>
  </Mui.ThemeProvider>;
}

export function EventTraceEntry(props) {
  return <EventTrace {...props} />;
}

export default withScreenPermissions(EventTraceEntry, SCREEN_ACCESS.monitoringEventTrace, "Event Trace");
